'use client';

import React from 'react';
import styled from '@benzinga/themetron';
import { Icon } from '../Icon';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';

export interface CollapseProps {
  children: React.ReactElement | React.ReactElement[];
  header?: React.ReactNode;
  hideHeaderIcon?: boolean;
  onItemClick?: (newIndex: number) => void;
  defaultCollapsed?: boolean;
  onToggle?: (func: (value: boolean) => void) => void;
}

export const Collapse: React.FC<CollapseProps> = ({
  children,
  defaultCollapsed = false,
  header,
  hideHeaderIcon = false,
  onToggle,
}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed);
  const [contentHeight, setContentHeight] = React.useState('unset');

  React.useEffect(() => {
    setIsCollapsed(defaultCollapsed);
  }, [defaultCollapsed]);

  const contentRef = React.useRef<HTMLDivElement>(null);
  const collapseActionClassname = isCollapsed ? 'collapsed' : 'expanded';

  React.useEffect(() => {
    if (contentRef.current) {
      const newContentHeight = contentRef.current?.clientHeight;
      if (newContentHeight) {
        setContentHeight(`${newContentHeight}px`);
      }
    }
  }, []);

  const handleToggle = () => {
    if (typeof onToggle === 'function') {
      onToggle((value: boolean) => {
        setIsCollapsed(value);
      });
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  return (
    <CollapseContainer className={`collapse-container ${collapseActionClassname}`}>
      <div
        aria-expanded={!isCollapsed}
        className="collapse-panel-header"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
      >
        {!hideHeaderIcon && <Icon className={`chevron-icon ${collapseActionClassname}`} icon={faChevronRight} />}
        <span>{header}</span>
      </div>
      <div
        className="collapse-panel-content"
        style={{
          maxHeight: !isCollapsed ? contentHeight : 0,
        }}
      >
        <div ref={contentRef}>{children}</div>
      </div>
    </CollapseContainer>
  );
};

const CollapseContainer = styled.div`
  &.collapse-container {
    > {
      border: 1px solid ${({ theme }) => theme.colors.border};
      border-radius: ${({ theme }) => theme.borderRadius.sm};

      &.expanded {
        .collapse-panel-header {
          border-bottom: 1px solid ${({ theme }) => theme.colors.border};
        }
      }
    }
    .collapse-panel-header {
      display: block;
      width: 100%;
      padding: 10px 0;
      background-color: ${({ theme }) => theme.colorPalette.gray50};
      font-size: ${({ theme }) => theme.fontSize.base};
      text-align: left;

      .chevron-icon {
        margin-right: 15px;
        font-size: ${({ theme }) => theme.fontSize.sm};
        transition: 0.24s transform;
        display: inline-block;

        &.expanded {
          transform: rotate(90deg);
        }
      }
    }

    .collapse-panel-content {
      font-size: ${({ theme }) => theme.fontSize.base};
      transition: 0.3s max-height;
      overflow: hidden;
    }

    &.collapsed {
      .collapse-panel-content {
        overflow: hidden;
        transition: 0.4s max-height;

        > div {
          overflow: auto;
        }
      }
    }
  }
`;

export default Collapse;
